/**
 * Typed client for the Kroupy backend.
 *
 * One small fetch wrapper plus the wire types mirrored from api/models/schemas.py. The types are
 * hand-kept (not generated) because the surface is tiny and explicit beats a build-time codegen
 * step here; if the contract grows, swap to an OpenAPI generator. Geometry is GeoJSON (RFC 7946)
 * so it feeds MapLibre sources directly.
 */

const API_BASE_URL: string = (
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:8000'
).replace(/\/$/, '');

// --- Wire types (mirror api/models/schemas.py) ---------------------------------------------------

export interface HailFeature {
  type: 'Feature';
  geometry: GeoJSON.Polygon;
  /** Always carries hail_size_cm; area_km2 when polygonized; F-CONF beam-coverage fields when known. */
  properties: {
    hail_size_cm?: number;
    area_km2?: number;
    /** True = size is a LOWER BOUND (storm far from radar, beam over the 0 C level). */
    size_underestimated?: boolean;
    /** Size reliability 0..1 (1 = full 0 C..-20 C band sampled). */
    size_confidence?: number;
    radar_range_km?: number;
    [key: string]: unknown;
  };
}

export interface HailEventCollection {
  type: 'FeatureCollection';
  features: HailFeature[];
  /** Detection provenance, notably isotherm_source ('seasonal-climatology' | 'synthetic' | real). */
  metadata: { isotherm_source?: string; [key: string]: unknown };
}

export interface RadarOverlay {
  /** data:image/png;base64,... composite reflectivity PNG. */
  image_data_uri: string;
  /** EPSG:4326 extent [west, south, east, north]. */
  bounds: [number, number, number, number];
}

export interface HailEventSummary {
  id: number;
  occurred_at: string;
  source: string;
  hail_size_cm: number | null;
  area_km2: number | null;
  isotherm_source: string | null;
  /** Size reliability 0..1 (1 = full 0 C..-20 C band sampled); null when unknown (F-CONF). */
  size_confidence: number | null;
  /** Distance of the cell from the detecting radar (km). */
  radar_range_km: number | null;
  /** Lowest sweep beam-centre height ASL at the cell's range (m). */
  lowest_beam_height_m: number | null;
  /**
   * Beam-COVERAGE flag: true = the lowest beam overshoots the 0 C level, so hail_size_cm is a LOWER
   * BOUND (storm far from radar). On a NORMAL event this only drives a 'could be larger' note — it
   * does NOT by itself suppress alerts (see size_underestimated_event).
   */
  size_underestimated: boolean;
  /**
   * Event-TYPE flag: true ONLY for a sub-gate cell kept solely because of poor coverage (the
   * underestimated path). This is the authoritative review-only marker — the event is map/dashboard
   * only and generated NO auto-preview message (F-CONF / Martin's product decision). The UI branches
   * on THIS for the 'PODHODNOCENO' review banner vs the softer 'could be larger' note — never on a
   * hardcoded size threshold (which could desync from the backend's configurable alert gate).
   */
  size_underestimated_event: boolean;
  /**
   * Complement of size_underestimated: true when the detection is CLOSE + RELIABLE enough to verify on
   * the ground (range <= the configured horizon, good beam coverage, >= the alert gate). A ground-truth
   * verification candidate — the sidebar shows a distinct, subtle 'validační kandidát' line, visually
   * different from the amber PODHODNOCENO cue.
   */
  validation_grade: boolean;
  /**
   * Human-gate timestamp (S320/S335): when a person confirmed the hail was real and the Places lead
   * search ran. Null = candidate only — the sidebar offers the confirm button instead of a lead list.
   */
  leads_confirmed_at: string | null;
  geometry: GeoJSON.Polygon;
}

/**
 * A contracted PARTNER flagged in this event's swath (F-4C): its spatial match + flag state. No
 * outreach message is generated for anyone; the partner's consented contact fields are surfaced for
 * MANUAL contact, with the size `band` and `status` ('preview' — never sent).
 */
export interface PartnerAlert {
  shop_id: number;
  name: string;
  inside: boolean;
  distance_km: number;
  geometry: GeoJSON.Point;
  /** Partner website if available, else null. */
  website: string | null;
  /** Partner consented email, or null. */
  contact_email: string | null;
  /** Partner consented phone, or null. */
  contact_phone: string | null;
  alert_id: number | null;
  channel: string | null;
  /** Hail size-band index at emission (escalation tracking). */
  band: number | null;
  status: string | null;
}

/**
 * A non-contracted Google Places LEAD of a CONFIRMED event (S335): pin + deep-link, nothing more.
 * Carries NO name/contact/rating (EEA terms cap what may leave Google) and NO message — leads are
 * never auto-contacted (§7 zákona 480/2004 Sb.). `maps_url` opens the full Google business card
 * (name, phone, web) for free.
 */
export interface Lead {
  place_id: string;
  /** OUR derived trade label (deterministic classifier, S335): 'Karosárna/lakovna', 'Autosklo',
   * 'STK', 'Autoservis' or generic 'Servis'. Derived data — displayable where Places content is
   * not. Irrelevant trades (pneu, fólie, myčky, prodej) never reach the wire at all. */
  category: string;
  /** True for the hail-damage core trades (karosárna/lakovna, autosklo) — listed/highlighted first. */
  priority: boolean;
  inside: boolean;
  distance_km: number;
  geometry: GeoJSON.Point;
  /** Free Google Maps deep-link opening the place's full business card. */
  maps_url: string;
}

/** A tracked cell's recent movement: speed (km/min) and compass bearing (0 = N, 90 = E). */
export interface MovementVector {
  speed_kmpm: number;
  bearing_deg: number;
}

/** The current swath translated forward to one lead time, with its confidence band. */
export interface PredictedSwath {
  lead_min: number;
  confidence: 'high' | 'medium' | 'low';
  geometry: GeoJSON.Polygon;
}

/**
 * A shop a predicted future swath will hit. `eta_min` is relative to `ForecastBlock.forecast_origin`
 * (the cell's last scan), not to "now". `direct` is true when the shop sits INSIDE the predicted
 * swath (a direct hit) and false when it is only near the track (may be missed).
 * `predicted_hail_size_cm` is the cell's CURRENT size carried forward — MOVEMENT only, not an
 * intensity forecast (see ForecastBlock.intensity_predicted).
 */
export interface PredictedImpact {
  shop_id: number;
  kind: 'partner' | 'public';
  eta_min: number;
  direct: boolean;
  predicted_hail_size_cm: number | null;
  confidence: 'high' | 'medium' | 'low';
}

/**
 * Pre-impact movement nowcast for a tracked cell (F-4B): where the swath goes next + per-shop ETAs.
 * `forecast_origin` is the timestamp the ETAs are measured FROM (the cell's last scan), NOT "now" —
 * the UI uses it so "+25 min" cannot be mistaken as 25 min from now on a stale forecast.
 * `intensity_predicted` is ALWAYS false — movement is extrapolated, intensity change is NOT predicted;
 * `caveat` is the same warning in human form to surface in the UI.
 */
export interface ForecastBlock {
  movement: MovementVector;
  forecast_origin: string;
  predicted_swaths: PredictedSwath[];
  impacted_shops: PredictedImpact[];
  intensity_predicted: false;
  caveat: string;
}

export interface HailEventDetail extends HailEventSummary {
  /** Pre-impact forecast, or null when the cell has no movement vector yet (<2 history points). */
  forecast: ForecastBlock | null;
  /** Contracted partners matched in the swath (flagged, no message). Empty for a review-only
   * underestimated event (its partners ride in `review_only_partners` instead). */
  partner_alerts: PartnerAlert[];
  /** In-swath partners of a review-only underestimated event; kept OUT of the alert queue so it
   * shows no count for a suppressed event. Empty for a normal event. */
  review_only_partners: PartnerAlert[];
  /** Akvizicni leady z Places — map+list only (leads are never auto-contacted, §7). */
  leads: Lead[];
}

/**
 * Result of POST /api/hail-events/{id}/confirm-leads — the human gate (S320/S335). `api_calls` is
 * what THIS confirmation spent from the Places budget (0 on the idempotent re-confirm).
 */
export interface ConfirmLeadsReport {
  event_id: number;
  leads_found: number;
  api_calls: number;
  already_confirmed: boolean;
  /** True when the rural fallback re-ran the search with the larger margin. */
  zone_expanded: boolean;
  /** Human note when the fallback could not (fully) run (e.g. daily budget exhausted). */
  note: string | null;
}

/**
 * Result of POST /api/partners/sync (F-PRT-SYNC): the diff of one reconcile against the live
 * Karosář.cz page — branches added / updated / soft-deactivated, the resulting active count, and the
 * last-synced timestamp (ISO; null if never). On a fail-soft no-op (fetch failed/empty) the counts are
 * all zero and `last_synced_at` carries the previous value.
 */
export interface PartnerSyncReport {
  added: number;
  updated: number;
  deactivated: number;
  total_active: number;
  last_synced_at: string | null;
}

/** Partner-pool freshness for the header (GET /api/partners/status): active count + last-synced. */
export interface PartnersStatus {
  total_active: number;
  last_synced_at: string | null;
}

/**
 * Liveness + data freshness (GET /health — outside /api by design, it is the ops liveness probe).
 * ``last_scan_age_seconds`` is the age of the freshest PROCESSED ČHMÚ scan; null = no scan yet
 * (fresh DB / scheduler off). The endpoint always answers 200 and leaves "too old" to the consumer.
 */
export interface HealthStatus {
  status: string;
  last_scan_ts: string | null;
  last_scan_age_seconds: number | null;
}

/** The logged-in account (login response + GET /api/auth/me). Never carries the password hash. */
export interface UserProfile {
  id: number;
  email: string;
  display_name: string | null;
}

// --- Fetch wrapper -------------------------------------------------------------------------------

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    options?: ErrorOptions,
  ) {
    super(message, options);
    this.name = 'ApiError';
  }
}

/**
 * FastAPI puts the human-readable reason into the error body's `detail` — surfacing it beats the
 * bare "POST /path → 429" (e.g. the budget message says whether tomorrow fixes it). Returns null
 * for a missing/non-string detail or a non-JSON body (proxy error page), so callers can fall back.
 */
async function errorDetail(resp: Response): Promise<string | null> {
  try {
    const body: unknown = await resp.json();
    if (typeof body === 'object' && body !== null && 'detail' in body) {
      const detail = body.detail;
      if (typeof detail === 'string' && detail !== '') return detail;
    }
  } catch {
    // unparsable body — the status-line fallback below is all we have
  }
  return null;
}

// A 401 can surface on ANY call once the session cookie expires. The login gate registers one
// handler here so the re-gate logic lives in a single place instead of every call site; it must be
// idempotent (one tick fires five parallel requests → five 401s at once). Wired by main.ts.
let onUnauthorized: (() => void) | null = null;

/** Register the module-wide 401 hook (null to clear). Fires on every 401 the API returns. */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let resp: Response;
  try {
    // credentials 'include', not 'same-origin': the Vite dev server (:5173 → :8000) is
    // cross-origin, and 'same-origin' would silently drop the session cookie there.
    resp = await fetch(`${API_BASE_URL}${path}`, { credentials: 'include', ...init });
  } catch (cause) {
    throw new ApiError(`Síť nedostupná (${API_BASE_URL}${path})`, 0, { cause });
  }
  if (!resp.ok) {
    if (resp.status === 401) onUnauthorized?.();
    const detail = await errorDetail(resp);
    throw new ApiError(detail ?? `${init?.method ?? 'GET'} ${path} → ${resp.status}`, resp.status);
  }
  return resp.json() as Promise<T>;
}

/** Like request() but for bodyless responses (204 logout — resp.json() would throw there). */
async function requestVoid(path: string, init?: RequestInit): Promise<void> {
  let resp: Response;
  try {
    resp = await fetch(`${API_BASE_URL}${path}`, { credentials: 'include', ...init });
  } catch (cause) {
    throw new ApiError(`Síť nedostupná (${API_BASE_URL}${path})`, 0, { cause });
  }
  if (!resp.ok) {
    if (resp.status === 401) onUnauthorized?.();
    throw new ApiError(`${init?.method ?? 'GET'} ${path} → ${resp.status}`, resp.status);
  }
}

/** Live hail polygons detected on the latest scan. Empty features = no current hail (valid). */
export function getRadarHailEvents(): Promise<HailEventCollection> {
  return request<HailEventCollection>('/api/radar/hail-events');
}

/**
 * Latest-scan composite reflectivity overlay, or null when the backend has no scan yet (HTTP 204).
 * 204 is a normal "clear store" answer, not an error, so it is mapped to null rather than thrown.
 */
export async function getRadarOverlay(): Promise<RadarOverlay | null> {
  let resp: Response;
  try {
    // This hand-rolled fetch (204 → null mapping) must carry the SAME credentials + 401 hook as
    // request() — without them the radar layer alone would 401 forever while the rest works.
    resp = await fetch(`${API_BASE_URL}/api/radar/overlay`, { credentials: 'include' });
  } catch (cause) {
    throw new ApiError(`Síť nedostupná (${API_BASE_URL}/api/radar/overlay)`, 0, { cause });
  }
  if (resp.status === 204) return null;
  if (!resp.ok) {
    if (resp.status === 401) onUnauthorized?.();
    const detail = await errorDetail(resp);
    throw new ApiError(detail ?? `GET /api/radar/overlay → ${resp.status}`, resp.status);
  }
  return resp.json() as Promise<RadarOverlay>;
}

/** Stored lead-gen hail events (newest first). */
export function listHailEvents(): Promise<HailEventSummary[]> {
  return request<HailEventSummary[]>('/api/hail-events');
}

/**
 * The shop arrays may be absent if the response predates F-CONF (a backend running older code before
 * a restart) — model them as optional on the wire so the normaliser below is type-checked, not a cast.
 */
type RawEventDetail = Omit<HailEventDetail, 'partner_alerts' | 'review_only_partners' | 'leads'> &
  Partial<Pick<HailEventDetail, 'partner_alerts' | 'review_only_partners' | 'leads'>>;

/**
 * Full detail for one stored event: swath + partner alert queue + public leads (no messages).
 * The three shop arrays are defaulted to ``[]`` so a missing key (e.g. a pre-F-CONF backend that
 * never sends ``review_only_partners``) can never throw "not iterable" when the sidebar maps them.
 */
export async function getHailEventDetail(id: number): Promise<HailEventDetail> {
  const raw = await request<RawEventDetail>(`/api/hail-events/${id}`);
  return {
    ...raw,
    partner_alerts: raw.partner_alerts ?? [],
    review_only_partners: raw.review_only_partners ?? [],
    leads: raw.leads ?? [],
  };
}

/**
 * THE human gate (S320/S335): confirm the event's hail is real and run the Places lead search. The
 * only client action that spends Places API budget; idempotent on a re-confirm.
 */
export function confirmLeads(eventId: number): Promise<ConfirmLeadsReport> {
  return request<ConfirmLeadsReport>(`/api/hail-events/${eventId}/confirm-leads`, {
    method: 'POST',
  });
}

/** Reconcile the partner pool against the live Karosář.cz page and return the diff (F-PRT-SYNC). */
export function syncPartners(): Promise<PartnerSyncReport> {
  return request<PartnerSyncReport>('/api/partners/sync', { method: 'POST' });
}

/** Partner-pool freshness (active count + when it last synced) for the header line. */
export function getPartnersStatus(): Promise<PartnersStatus> {
  return request<PartnersStatus>('/api/partners/status');
}

/** Scan freshness for the header live indicator. */
export function getHealth(): Promise<HealthStatus> {
  return request<HealthStatus>('/health');
}

/**
 * Open a session. The backend answers 401 with ONE shared body for an unknown e-mail and a wrong
 * password (anti-enumeration), so the login card shows a single generic message for both.
 */
export function login(email: string, password: string): Promise<UserProfile> {
  return request<UserProfile>('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
}

/** Close the session (204 — hence requestVoid; request() would choke on the empty body). */
export function logout(): Promise<void> {
  return requestVoid('/api/auth/logout', { method: 'POST' });
}

/** The session probe the login gate runs on startup; rejects with a 401 ApiError when logged out. */
export function getMe(): Promise<UserProfile> {
  return request<UserProfile>('/api/auth/me');
}
